import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Plus, ShoppingCart, Trash2, Lock, Unlock } from 'lucide-react';
import { FormSheet } from '@/components/common/FormSheet';
import { FormField } from '@/components/common/FormField';
import { DialogEyebrow, SectionHeading } from '@/components/common/DialogPrimitives';
import { DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { customersService } from '@/services/customers';
import { taxSettingsService } from '@/services/platform/tax-settings';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import {
  inventoryService,
  itemPricesService,
  warehousesService,
} from '@/services/inventory';
import { ordersService, type CreateOrderPayload } from '@/services/orders';
import { fmt } from '@/lib/format';
import { pickerLabel } from '@/lib/inventory-label';
import type { InventoryItem, ItemUnit } from '@/types/inventory';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Optional pre-fill — used when "Book another for this customer" is invoked. */
  prefillCustomerId?: string;
}

interface DraftLine {
  key: number;
  itemId: string;
  /** Quantity in selected UoM. Converted to base before submit. */
  quantity: string;
  /** Selected alternate UoM id; null = base unit. */
  unitId: string | null;
  /** Optional manual unit price override (in INR). Empty string = use catalog selling price. */
  unitPrice: string;
}

let LINE_KEY = 1;
const newLine = (): DraftLine => ({
  key: LINE_KEY++,
  itemId: '',
  quantity: '',
  unitId: null,
  unitPrice: '',
});

/**
 * Sales-order create form. Wraps the BE `POST /orders` endpoint:
 *   - Customer picker (existing customers only — quick-create lives in the
 *     Customers module; this form keeps booking focused).
 *   - Warehouse picker (which physical location the order ships from).
 *   - Multi-line item rows with UoM dropdown — qty entered in any defined
 *     UoM gets converted to base before submit (consistent with the
 *     inward-receipt flow).
 *   - Optional unit-price override per line; otherwise the BE leaves the
 *     lineTotal null and computes from the catalog `sellingPrice` later.
 *   - "Reserve stock immediately" toggle — when on (default), the BE opens
 *     `StockReservation`s right away so the inventory is locked.
 */
export function OrderFormSheet({ open, onOpenChange, prefillCustomerId }: Props) {
  const qc = useQueryClient();

  const me = useCurrentUser();
  const tenantCurrency = me?.tenantCurrency ?? 'INR';
  const [customerId, setCustomerId] = useState<string>('');
  const [warehouseId, setWarehouseId] = useState<string>('');
  const [lines, setLines] = useState<DraftLine[]>([newLine()]);
  const [notes, setNotes] = useState('');
  const [externalRef, setExternalRef] = useState('');
  const [reserveStock, setReserveStock] = useState(true);
  const [paymentMode, setPaymentMode] = useState<'PREPAID' | 'COD'>('PREPAID');
  // M-money-model — order-level money rows. Stored as strings so the
  // operator can type freely; converted to numbers on submit. roundOff
  // can be negative.
  const [shippingCharge, setShippingCharge] = useState('');
  const [discountAmount, setDiscountAmount] = useState('');
  const [roundOff, setRoundOff] = useState('');
  const [additionalCharges, setAdditionalCharges] = useState<
    Array<{ key: number; label: string; amount: string }>
  >([]);
  // Order currency: defaults to picked customer's defaultCurrency if set,
  // else tenant base. Operator can override manually.
  const [currency, setCurrency] = useState<string>(tenantCurrency);
  const [currencyManuallySet, setCurrencyManuallySet] = useState(false);

  // ── Lookups ──────────────────────────────────────────────────────────
  const customersQuery = useQuery({
    queryKey: ['customers', { forSelect: true }],
    queryFn: () => customersService.list({ status: 'ACTIVE', pageSize: 200 }),
    enabled: open,
    staleTime: 60_000,
  });

  // M-money-model — tenant's tax-pricing mode drives the badge + unitPrice
  // hint operators see when entering line items. INCLUSIVE = "this price
  // is MRP"; EXCLUSIVE = "tax adds on top at invoice".
  const taxSettingsQuery = useQuery({
    queryKey: ['tenant', 'tax-settings'],
    queryFn: taxSettingsService.get,
    enabled: open,
    staleTime: 5 * 60_000,
  });
  const taxMode = taxSettingsQuery.data?.taxPricingMode ?? 'EXCLUSIVE';
  const isInclusiveOrder = taxMode === 'INCLUSIVE';

  const itemsQuery = useQuery({
    queryKey: ['inventory', 'items', { status: 'ACTIVE', forSelect: 'orders' }],
    queryFn: () => inventoryService.list({ status: 'ACTIVE', pageSize: 200, pickable: true }),
    enabled: open,
    staleTime: 30_000,
  });

  const warehousesQuery = useQuery({
    queryKey: ['inventory', 'warehouses'],
    queryFn: () => warehousesService.list(),
    enabled: open,
    staleTime: 60_000,
  });

  const customers = customersQuery.data?.items ?? [];
  const items = itemsQuery.data?.items ?? [];
  const warehouses = warehousesQuery.data ?? [];
  const itemById = useMemo(() => {
    const m = new Map<string, InventoryItem>();
    for (const it of items) m.set(it.id, it);
    return m;
  }, [items]);

  // Reset on open / prefill.
  useEffect(() => {
    if (!open) return;
    setCustomerId(prefillCustomerId ?? '');
    setWarehouseId('');
    setLines([newLine()]);
    setNotes('');
    setExternalRef('');
    setReserveStock(true);
    setCurrency(tenantCurrency);
    setCurrencyManuallySet(false);
    setShippingCharge('');
    setDiscountAmount('');
    setRoundOff('');
    setAdditionalCharges([]);
  }, [open, prefillCustomerId, tenantCurrency]);

  // Selected customer for hero badges.
  const selectedCustomer = customers.find((c) => c.id === customerId) ?? null;

  // When the operator picks a customer with a defaultCurrency override,
  // sync the order currency to it (unless they've already adjusted it
  // by hand). This is the "USD export customer" UX — pick the customer,
  // currency flips automatically.
  useEffect(() => {
    if (currencyManuallySet) return;
    const target = selectedCustomer?.defaultCurrency || tenantCurrency;
    setCurrency(target);
  }, [selectedCustomer, tenantCurrency, currencyManuallySet]);

  // ── Submit ───────────────────────────────────────────────────────────
  const mutation = useMutation({
    mutationFn: async () => {
      // Build base-unit quantities by pulling each line's UoM list from cache.
      const built: CreateOrderPayload['items'] = [];
      for (const line of lines) {
        if (!line.itemId || !Number(line.quantity)) continue;
        let units = qc.getQueryData<ItemUnit[]>(['inventory', 'units', line.itemId]);
        if (units === undefined) {
          units = await qc.fetchQuery({
            queryKey: ['inventory', 'units', line.itemId],
            queryFn: () => inventoryService.listUnits(line.itemId),
            staleTime: 30_000,
          });
        }
        const baseQty = computeBaseQty(line, units);
        if (baseQty <= 0) continue;
        built.push({
          itemId: line.itemId,
          quantity: baseQty,
          unitPrice: line.unitPrice ? Number(line.unitPrice) : undefined,
        });
      }
      if (built.length === 0) throw new Error('Add at least one line with quantity > 0.');

      // M-money-model — only send `money` when at least one field is
      // non-empty. Keeps the payload small for the common no-charges case.
      const moneyRows = additionalCharges
        .map((r) => ({ label: r.label.trim(), amount: Number(r.amount) }))
        .filter((r) => r.label.length > 0 && Number.isFinite(r.amount));
      const shipping = Number(shippingCharge);
      const discount = Number(discountAmount);
      const round = Number(roundOff);
      const money =
        shipping || discount || round || moneyRows.length > 0
          ? {
              shippingCharge: Number.isFinite(shipping) && shipping ? shipping : undefined,
              discountAmount: Number.isFinite(discount) && discount ? discount : undefined,
              roundOff: Number.isFinite(round) && round ? round : undefined,
              additionalCharges: moneyRows.length > 0 ? moneyRows : undefined,
            }
          : undefined;

      const payload: CreateOrderPayload = {
        customerId,
        warehouseId,
        items: built,
        externalOrderRef: externalRef.trim() || undefined,
        notes: notes.trim() || undefined,
        reserveStock,
        currency: currency?.toUpperCase() || undefined,
        paymentMode,
        money,
      };
      return ordersService.create(payload);
    },
    onSuccess: (order) => {
      toast.success(`Order ${order.orderNumber} created`);
      qc.invalidateQueries({ queryKey: ['orders'] });
      qc.invalidateQueries({ queryKey: ['inventory', 'stock'] });
      qc.invalidateQueries({ queryKey: ['customers'] });
      onOpenChange(false);
    },
    onError: (err: unknown) =>
      toast.error(err instanceof Error ? err.message : 'Could not create order'),
  });

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!customerId || !warehouseId) return;
    mutation.mutate();
  };

  const addLine = () => setLines((ls) => [...ls, newLine()]);
  const removeLine = (key: number) =>
    setLines((ls) => (ls.length === 1 ? ls : ls.filter((l) => l.key !== key)));

  // Pre-fetch per-currency prices for every line item when the order
  // is in a non-base currency so the totals preview can resolve them
  // from the cache. Re-runs on currency change.
  const isBaseCurrency = currency.toUpperCase() === tenantCurrency.toUpperCase();
  useEffect(() => {
    if (isBaseCurrency) return;
    const ids = Array.from(
      new Set(lines.map((l) => l.itemId).filter((id): id is string => !!id)),
    );
    for (const id of ids) {
      qc.ensureQueryData({
        queryKey: ['inventory', 'item-prices', id],
        queryFn: () => itemPricesService.list(id),
        staleTime: 30_000,
      });
    }
  }, [isBaseCurrency, lines, qc]);

  // ── Totals preview ───────────────────────────────────────────────────
  // For non-base currencies, pull the per-currency selling price from
  // the ItemPrice cache. Lines that have no row for the picked currency
  // (and no operator-supplied unitPrice) count as "unpriced" — the BE
  // will leave them at unitPrice=null on create, operator fills later.
  const totals = useMemo(() => {
    let lineCount = 0;
    let estTotal = 0;
    let hasUnpriced = false;
    for (const line of lines) {
      const qty = Number(line.quantity);
      if (!line.itemId || !Number.isFinite(qty) || qty <= 0) continue;
      lineCount += 1;
      const item = itemById.get(line.itemId);
      const explicit = line.unitPrice ? Number(line.unitPrice) : NaN;
      let price: number | null = null;
      if (Number.isFinite(explicit)) {
        price = explicit;
      } else if (isBaseCurrency) {
        price = item?.sellingPrice ?? null;
      } else {
        // Look up the per-currency ItemPrice from the cache (fetched
        // by the priceQueries below). If the row hasn't been fetched
        // yet, treat as unpriced — UX shows a transient "unpriced
        // lines" hint until the cache hydrates.
        const prices = qc.getQueryData<{ currency: string; sellingPrice: number }[]>([
          'inventory',
          'item-prices',
          line.itemId,
        ]);
        const row = prices?.find(
          (p) => p.currency.toUpperCase() === currency.toUpperCase(),
        );
        price = row ? Number(row.sellingPrice) : null;
      }
      if (price == null) {
        hasUnpriced = true;
      } else {
        const units = qc.getQueryData<ItemUnit[]>(['inventory', 'units', line.itemId]) ?? [];
        const u = line.unitId ? units.find((x) => x.id === line.unitId) : null;
        const baseQty = u ? qty * Number(u.conversionFactor) : qty;
        estTotal += baseQty * price;
      }
    }
    // M-money-model — preview grand total alongside the subtotal so
    // the operator sees what the customer pays before submitting.
    const shipping = Number(shippingCharge) || 0;
    const discount = Number(discountAmount) || 0;
    const round = Number(roundOff) || 0;
    const additionalSum = additionalCharges.reduce(
      (s, r) => (r.label.trim() && Number.isFinite(Number(r.amount)) ? s + Number(r.amount) : s),
      0,
    );
    const grandTotal = estTotal + shipping + additionalSum - discount + round;
    return { lineCount, estTotal, hasUnpriced, grandTotal };
  }, [
    lines,
    itemById,
    qc,
    currency,
    isBaseCurrency,
    shippingCharge,
    discountAmount,
    roundOff,
    additionalCharges,
  ]);

  const valid = customerId && warehouseId && totals.lineCount > 0;

  return (
    <FormSheet
      open={open}
      onOpenChange={onOpenChange}
      headerSlot={
        <div className="flex items-start gap-4">
          <span
            aria-hidden="true"
            className="grid h-14 w-14 shrink-0 place-items-center rounded-lg bg-gradient-to-br from-primary/10 to-primary/5 text-primary ring-1 ring-inset ring-primary/15"
          >
            <ShoppingCart className="h-6 w-6" />
          </span>
          <div className="min-w-0 flex-1 space-y-1.5">
            <DialogEyebrow>New sales order</DialogEyebrow>
            <DialogTitle className="text-[18px] font-semibold leading-tight tracking-tight">
              Book an order
            </DialogTitle>
            {selectedCustomer ? (
              <div className="flex flex-wrap items-center gap-1.5 pt-0.5">
                <Badge variant="muted" className="text-[10px]">
                  {selectedCustomer.name}
                </Badge>
                <Badge variant="muted" className="font-mono text-[10px]">
                  {selectedCustomer.code}
                </Badge>
                {selectedCustomer.gstin && (
                  <Badge variant="muted" className="font-mono text-[10px]">
                    GSTIN
                  </Badge>
                )}
              </div>
            ) : (
              <DialogDescription className="text-[12.5px] leading-relaxed">
                Pick the customer and warehouse, add line items, and submit. Stock can be
                reserved immediately or left for later.
              </DialogDescription>
            )}
          </div>
        </div>
      }
      title=""
      submitLabel="Create order"
      onSubmit={onSubmit}
      isSubmitting={mutation.isPending}
      error={mutation.error}
      size="5xl"
      fixedHeight="xl"
    >
      <div className="space-y-6">
        {/* ── Header — customer + warehouse + ref ───────────────────── */}
        <section className="space-y-3">
          <SectionHeading title="Order header" />
          <div className="grid grid-cols-12 gap-4">
            <div className="col-span-4">
              <FormField label="Customer" htmlFor="customer" required>
                <Select value={customerId} onValueChange={setCustomerId}>
                  <SelectTrigger id="customer" className="h-9">
                    <SelectValue placeholder={customersQuery.isLoading ? 'Loading…' : 'Select customer'} />
                  </SelectTrigger>
                  <SelectContent>
                    {customers.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        <span>{c.name}</span>
                        <span className="ml-2 font-mono text-[10.5px] text-muted-foreground">
                          {c.code}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormField>
            </div>
            <div className="col-span-3">
              <FormField label="Warehouse" htmlFor="warehouse" required>
                <Select value={warehouseId} onValueChange={setWarehouseId}>
                  <SelectTrigger id="warehouse" className="h-9">
                    <SelectValue placeholder={warehousesQuery.isLoading ? 'Loading…' : 'Select'} />
                  </SelectTrigger>
                  <SelectContent>
                    {warehouses.map((w) => (
                      <SelectItem key={w.id} value={w.id}>
                        <span className="font-mono text-[10.5px] text-muted-foreground">{w.code}</span>
                        <span className="ml-2">{w.name}</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormField>
            </div>
            <div className="col-span-2">
              <FormField
                label="Currency"
                htmlFor="currency"
                hint={
                  selectedCustomer?.defaultCurrency
                    ? `Customer default: ${selectedCustomer.defaultCurrency}`
                    : `Tenant base: ${tenantCurrency}`
                }
              >
                <Input
                  id="currency"
                  value={currency}
                  onChange={(e) => {
                    setCurrency(e.target.value.toUpperCase());
                    setCurrencyManuallySet(true);
                  }}
                  placeholder={tenantCurrency}
                  maxLength={3}
                  className="h-9 font-mono uppercase"
                  autoComplete="off"
                />
              </FormField>
            </div>
            <div className="col-span-3">
              <FormField label="External ref" htmlFor="externalRef" hint="PO / portal #">
                <Input
                  id="externalRef"
                  value={externalRef}
                  onChange={(e) => setExternalRef(e.target.value)}
                  placeholder="PO-2026-0042"
                  className="h-9"
                />
              </FormField>
            </div>
          </div>
        </section>

        {/* ── Lines ──────────────────────────────────────────────────── */}
        <section className="space-y-3">
          <div className="flex items-end justify-between gap-3">
            <SectionHeading
              title="Line items"
              hint={`${totals.lineCount} valid · est. ${fmt.money(totals.estTotal, currency)}${totals.hasUnpriced ? ' + unpriced lines' : ''}`}
            />
            <Badge
              variant="muted"
              className="text-[10px]"
              title={
                isInclusiveOrder
                  ? 'Unit prices are tax-inclusive (MRP). Change in Settings → Tax preferences.'
                  : 'Unit prices are tax-exclusive — tax is added at invoice. Change in Settings → Tax preferences.'
              }
            >
              {isInclusiveOrder ? 'Tax incl. (MRP)' : 'Tax excl.'}
            </Badge>
          </div>
          <div className="space-y-2">
            {lines.map((l) => (
              <LineRow
                key={l.key}
                line={l}
                item={l.itemId ? itemById.get(l.itemId) ?? null : null}
                items={items}
                itemsLoading={itemsQuery.isLoading}
                canRemove={lines.length > 1}
                currency={currency}
                onChange={(next) =>
                  setLines((ls) => ls.map((x) => (x.key === l.key ? { ...x, ...next } : x)))
                }
                onRemove={() => removeLine(l.key)}
              />
            ))}
          </div>
          <Button type="button" variant="outline" size="sm" onClick={addLine}>
            <Plus className="h-3.5 w-3.5" />
            Add line
          </Button>
        </section>

        {/* ── Order totals — shipping / discount / additional / round-off ── */}
        <section className="space-y-3">
          <SectionHeading
            title="Order totals"
            hint={`Customer pays ${fmt.money(totals.grandTotal, currency)}${totals.hasUnpriced ? ' + unpriced lines' : ''}`}
          />
          <div className="grid grid-cols-12 gap-4">
            <div className="col-span-3">
              <FormField label="Shipping charge" htmlFor="shipping-charge">
                <Input
                  id="shipping-charge"
                  type="number"
                  inputMode="decimal"
                  step="0.01"
                  min="0"
                  value={shippingCharge}
                  onChange={(e) => setShippingCharge(e.target.value)}
                  placeholder="0.00"
                  className="h-9 text-right font-mono"
                />
              </FormField>
            </div>
            <div className="col-span-3">
              <FormField label="Discount" htmlFor="discount-amount">
                <Input
                  id="discount-amount"
                  type="number"
                  inputMode="decimal"
                  step="0.01"
                  min="0"
                  value={discountAmount}
                  onChange={(e) => setDiscountAmount(e.target.value)}
                  placeholder="0.00"
                  className="h-9 text-right font-mono"
                />
              </FormField>
            </div>
            <div className="col-span-3">
              <FormField
                label="Round off"
                htmlFor="round-off"
                hint="Can be negative."
              >
                <Input
                  id="round-off"
                  type="number"
                  inputMode="decimal"
                  step="0.01"
                  value={roundOff}
                  onChange={(e) => setRoundOff(e.target.value)}
                  placeholder="0.00"
                  className="h-9 text-right font-mono"
                />
              </FormField>
            </div>
            <div className="col-span-3 flex items-end">
              <div className="w-full rounded-md border border-border/70 bg-card px-3 py-2.5">
                <div className="text-[10.5px] uppercase tracking-wide text-muted-foreground">
                  Grand total
                </div>
                <div className="font-mono text-[14px] font-semibold">
                  {fmt.money(totals.grandTotal, currency)}
                </div>
              </div>
            </div>
          </div>

          {additionalCharges.length > 0 && (
            <div className="space-y-2">
              {additionalCharges.map((row, idx) => (
                <div key={row.key} className="grid grid-cols-12 gap-2">
                  <div className="col-span-6">
                    <Input
                      value={row.label}
                      onChange={(e) =>
                        setAdditionalCharges((rs) =>
                          rs.map((r, i) => (i === idx ? { ...r, label: e.target.value } : r)),
                        )
                      }
                      placeholder="Handling fee · Gift wrap · COD surcharge"
                      maxLength={64}
                      className="h-9"
                    />
                  </div>
                  <div className="col-span-4">
                    <Input
                      type="number"
                      inputMode="decimal"
                      step="0.01"
                      value={row.amount}
                      onChange={(e) =>
                        setAdditionalCharges((rs) =>
                          rs.map((r, i) => (i === idx ? { ...r, amount: e.target.value } : r)),
                        )
                      }
                      placeholder="0.00"
                      className="h-9 text-right font-mono"
                    />
                  </div>
                  <div className="col-span-2 flex items-center">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() =>
                        setAdditionalCharges((rs) => rs.filter((_, i) => i !== idx))
                      }
                      aria-label="Remove charge"
                    >
                      <Trash2 className="h-3.5 w-3.5 text-muted-foreground" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {additionalCharges.length < 20 && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() =>
                setAdditionalCharges((rs) => [
                  ...rs,
                  { key: Date.now() + rs.length, label: '', amount: '' },
                ])
              }
            >
              <Plus className="h-3.5 w-3.5" />
              Add charge
            </Button>
          )}
        </section>

        {/* ── Notes + reservation toggle + payment mode ────────────── */}
        <section className="grid grid-cols-12 gap-4">
          <div className="col-span-6">
            <FormField label="Notes" htmlFor="notes" hint="Optional — appears in the audit trail.">
              <Textarea
                id="notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Customer requested same-day dispatch · Bill to GSTIN address"
                rows={2}
              />
            </FormField>
          </div>
          <div className="col-span-3 flex items-end">
            <button
              type="button"
              onClick={() => setReserveStock((v) => !v)}
              className={`flex w-full items-center gap-2.5 rounded-md border bg-card px-3 py-2.5 text-left text-[12.5px] transition-colors ${
                reserveStock
                  ? 'border-primary/30 ring-1 ring-inset ring-primary/15'
                  : 'border-border/70 hover:border-border'
              }`}
            >
              {reserveStock ? (
                <Lock className="h-4 w-4 shrink-0 text-primary" />
              ) : (
                <Unlock className="h-4 w-4 shrink-0 text-muted-foreground" />
              )}
              <div className="flex-1">
                <div className="font-medium">
                  {reserveStock ? 'Reserve stock now' : 'Skip reservation'}
                </div>
                <div className="text-[11px] text-muted-foreground">
                  {reserveStock
                    ? 'Locks inventory immediately'
                    : 'Stock stays available until later'}
                </div>
              </div>
            </button>
          </div>
          <div className="col-span-3 flex items-end">
            <button
              type="button"
              onClick={() =>
                setPaymentMode((m) => (m === 'PREPAID' ? 'COD' : 'PREPAID'))
              }
              className={`flex w-full items-center gap-2.5 rounded-md border bg-card px-3 py-2.5 text-left text-[12.5px] transition-colors ${
                paymentMode === 'COD'
                  ? 'border-warning/40 ring-1 ring-inset ring-warning/20'
                  : 'border-border/70 hover:border-border'
              }`}
            >
              <div className="flex-1">
                <div className="font-medium">
                  {paymentMode === 'COD' ? 'Cash on delivery' : 'Prepaid'}
                </div>
                <div className="text-[11px] text-muted-foreground">
                  {paymentMode === 'COD'
                    ? 'Skips payment gate; courier collects on delivery'
                    : 'Waits for payment success before dispatch'}
                </div>
              </div>
            </button>
          </div>
        </section>

        {!valid && (
          <div className="rounded-md border border-warning/30 bg-warning/5 px-3 py-2 text-[11.5px] text-[hsl(38_85%_30%)]">
            Pick a customer, a warehouse, and at least one line with quantity &gt; 0.
          </div>
        )}
      </div>
    </FormSheet>
  );
}

// ── LineRow ─────────────────────────────────────────────────────────────

interface LineRowProps {
  line: DraftLine;
  item: InventoryItem | null;
  items: InventoryItem[];
  itemsLoading: boolean;
  canRemove: boolean;
  currency: string;
  onChange: (next: Partial<DraftLine>) => void;
  onRemove: () => void;
}

function LineRow({
  line,
  item,
  items,
  itemsLoading,
  canRemove,
  currency,
  onChange,
  onRemove,
}: LineRowProps) {
  const unitsQuery = useQuery({
    queryKey: ['inventory', 'units', line.itemId],
    queryFn: () => inventoryService.listUnits(line.itemId),
    enabled: !!line.itemId,
    staleTime: 30_000,
  });
  const units = unitsQuery.data ?? [];
  const baseUnit = item?.unitOfMeasure ?? '';

  // Auto-pick sales-default UoM when SKU first set.
  useEffect(() => {
    if (!line.itemId || !units.length) return;
    if (line.unitId !== null) return;
    const def = units.find((u) => u.isSalesDefault);
    if (def) onChange({ unitId: def.id });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [line.itemId, units.length]);

  const selectedUnit = line.unitId ? units.find((u) => u.id === line.unitId) ?? null : null;
  const rawQty = Number(line.quantity);
  const baseQty =
    Number.isFinite(rawQty) && rawQty > 0
      ? selectedUnit
        ? rawQty * Number(selectedUnit.conversionFactor)
        : rawQty
      : 0;

  const explicitPrice = line.unitPrice ? Number(line.unitPrice) : NaN;
  const effectivePrice = Number.isFinite(explicitPrice)
    ? explicitPrice
    : (item?.sellingPrice ?? null);
  const lineTotal =
    effectivePrice != null && baseQty > 0 ? baseQty * effectivePrice : null;

  return (
    <div className="grid grid-cols-12 gap-2 rounded-md border border-border/70 bg-card p-2">
      <div className="col-span-4">
        <Select
          value={line.itemId}
          onValueChange={(v) => onChange({ itemId: v, unitId: null })}
        >
          <SelectTrigger className="h-8 text-[12.5px]">
            <SelectValue placeholder={itemsLoading ? 'Loading SKUs…' : 'Select SKU'} />
          </SelectTrigger>
          <SelectContent>
            {items.map((it) => (
              <SelectItem key={it.id} value={it.id}>
                <span className="font-mono text-[11px] text-muted-foreground">{it.sku}</span>
                <span className="ml-2">{pickerLabel(it)}</span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="col-span-2">
        <Input
          type="number"
          inputMode="decimal"
          min={0}
          step="0.001"
          value={line.quantity}
          onChange={(e) => onChange({ quantity: e.target.value })}
          placeholder="Qty"
          className="h-8 text-right text-[12.5px] tabular-nums"
        />
      </div>
      <div className="col-span-2">
        <Select
          value={line.unitId ?? '__base'}
          onValueChange={(v) => onChange({ unitId: v === '__base' ? null : v })}
          disabled={!line.itemId}
        >
          <SelectTrigger className="h-8 font-mono text-[11.5px]">
            <SelectValue placeholder="UoM" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__base">
              <span className="font-mono">{baseUnit || '—'}</span>
              <span className="ml-2 text-muted-foreground">base</span>
            </SelectItem>
            {units.map((u) => (
              <SelectItem key={u.id} value={u.id}>
                <span className="font-mono">{u.code}</span>
                <span className="ml-2 text-muted-foreground">×{fmt.number(u.conversionFactor)}</span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="col-span-2">
        <Input
          type="number"
          inputMode="decimal"
          min={0}
          step="0.01"
          value={line.unitPrice}
          onChange={(e) => onChange({ unitPrice: e.target.value })}
          placeholder={item?.sellingPrice != null ? `${item.sellingPrice}` : 'Unit ₹'}
          className="h-8 text-right text-[12.5px] tabular-nums"
        />
      </div>
      <div className="col-span-1 text-right font-mono text-[12.5px] tabular-nums text-muted-foreground">
        {lineTotal != null ? fmt.money(lineTotal, currency) : '—'}
      </div>
      <div className="col-span-1 flex items-center justify-end">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={onRemove}
          disabled={!canRemove}
          className="h-7 w-7 text-muted-foreground hover:text-destructive"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </div>

      {selectedUnit && baseQty > 0 && baseUnit && (
        <div className="col-span-12 px-1 text-[10.5px] text-muted-foreground">
          ↳ reserves{' '}
          <span className="font-mono tabular-nums text-foreground">
            {fmt.number(baseQty)} {baseUnit}
          </span>
        </div>
      )}
    </div>
  );
}

// ── Helpers ─────────────────────────────────────────────────────────────

function computeBaseQty(line: DraftLine, units: ItemUnit[] | undefined): number {
  const raw = Number(line.quantity);
  if (!Number.isFinite(raw) || raw <= 0) return 0;
  if (!line.unitId) return raw;
  const u = units?.find((x) => x.id === line.unitId);
  if (!u) return raw;
  return raw * Number(u.conversionFactor);
}
